import React, { useState } from 'react';
import { Gift, Loader2, Zap, Clock } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ReferralTestTab = ({ token }) => {
  const [discordId, setDiscordId] = useState('');
  const [points, setPoints] = useState('5');
  const [msg, setMsg] = useState(null);
  const [busy, setBusy] = useState('');

  const headers = { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` };

  const setPointsReq = async () => {
    setMsg(null); setBusy('set');
    try {
      const res = await fetch(`${API_URL}/api/admin/referral/set-points`, {
        method: 'POST', headers,
        body: JSON.stringify({ discord_id: discordId.trim(), points: parseInt(points, 10) || 0 }),
      });
      const d = await res.json();
      if (res.ok) setMsg({ ok: true, text: `✅ ${d.invite_points} invitation(s) → réduction -${d.discount_percent}% · code ${d.promo_code}` });
      else setMsg({ ok: false, text: `❌ ${d.detail || 'Erreur'}` });
    } catch (e) { setMsg({ ok: false, text: '❌ Erreur réseau' }); } finally { setBusy(''); }
  };

  const validateNow = async () => {
    setMsg(null); setBusy('validate');
    try {
      const res = await fetch(`${API_URL}/api/admin/referral/validate-now`, { method: 'POST', headers });
      const d = await res.json();
      if (res.ok) setMsg({ ok: true, text: `✅ Validation forcée : ${d.validated} validée(s), ${d.cancelled} annulée(s)` });
      else setMsg({ ok: false, text: `❌ ${d.detail || 'Erreur'}` });
    } catch (e) { setMsg({ ok: false, text: '❌ Erreur réseau' }); } finally { setBusy(''); }
  };

  return (
    <div className="max-w-2xl" data-testid="referral-test-tab">
      <div className="flex items-center gap-2 mb-2">
        <Gift className="w-5 h-5 text-cyan-400" />
        <h2 className="text-xl font-bold text-white">Parrainage — outils de test</h2>
      </div>
      <p className="text-gray-400 text-sm mb-6">
        En temps normal, une invitation se valide après 5 jours. Ces outils servent à <b className="text-white">tester</b> le système
        sans attendre : fixe un nombre d'invitations à un membre, ou force la validation des invitations en attente.
      </p>

      {/* Fixer des invitations */}
      <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-4 mb-4">
        <div className="flex items-center gap-2 mb-3">
          <Zap className="w-4 h-4 text-cyan-400" />
          <h3 className="text-white font-semibold text-sm">Attribuer des invitations à un membre</h3>
        </div>
        <p className="text-gray-500 text-xs mb-3">
          Entre l'ID Discord du membre (clic droit sur son profil → « Copier l'identifiant », mode développeur activé).
          Paliers : 5 = -5%, 10 = -10%, 20 = -15%.
        </p>
        <div className="flex flex-col sm:flex-row gap-2">
          <input
            data-testid="reftest-discord-id"
            value={discordId}
            onChange={(e) => setDiscordId(e.target.value)}
            placeholder="ID Discord (ex: 123456789012345678)"
            className="flex-1 bg-zinc-950 border border-zinc-700 rounded-lg px-3 py-2 text-white text-sm focus:border-cyan-500 focus:outline-none"
          />
          <input
            data-testid="reftest-points"
            type="number" min="0"
            value={points}
            onChange={(e) => setPoints(e.target.value)}
            className="w-24 bg-zinc-950 border border-zinc-700 rounded-lg px-3 py-2 text-white text-sm focus:border-cyan-500 focus:outline-none"
          />
          <button
            onClick={setPointsReq}
            disabled={!discordId.trim() || busy === 'set'}
            data-testid="reftest-set-btn"
            className="inline-flex items-center justify-center gap-2 px-4 py-2 bg-cyan-500 text-white text-sm font-semibold rounded-lg hover:bg-cyan-400 transition-colors disabled:opacity-50"
          >
            {busy === 'set' ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Appliquer'}
          </button>
        </div>
      </div>

      {/* Forcer validation */}
      <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-4">
        <div className="flex items-center gap-2 mb-3">
          <Clock className="w-4 h-4 text-cyan-400" />
          <h3 className="text-white font-semibold text-sm">Forcer la validation des invitations en attente</h3>
        </div>
        <p className="text-gray-500 text-xs mb-3">
          Valide tout de suite toutes les invitations « en attente » (sans attendre les 5 jours), si le membre est toujours sur le serveur.
        </p>
        <button
          onClick={validateNow}
          disabled={busy === 'validate'}
          data-testid="reftest-validate-btn"
          className="inline-flex items-center gap-2 px-4 py-2 bg-zinc-800 text-white text-sm font-semibold rounded-lg hover:bg-zinc-700 transition-colors disabled:opacity-50"
        >
          {busy === 'validate' ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Valider maintenant'}
        </button>
      </div>

      {msg && (
        <div data-testid="reftest-msg" className={`mt-4 p-3 rounded-lg text-sm ${msg.ok ? 'bg-green-500/15 text-green-300 border border-green-600/40' : 'bg-red-500/15 text-red-300 border border-red-600/40'}`}>
          {msg.text}
        </div>
      )}
    </div>
  );
};

export default ReferralTestTab;
